import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Checkbox, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { AlertOn16Regular, ArrowExit20Regular, BranchFork16Regular, Building16Regular, CalendarLtr16Regular, Checkmark16Regular, Edit16Regular, Flag16Regular, Important16Regular, Person16Regular, Stethoscope20Regular, Warning16Regular } from '@fluentui/react-icons';
import { ProtocolConsumptionConditionEnum } from '../../../models/Enums/ProtocolConsumptionConditionEnum';
import { useTranslation } from 'react-i18next';

import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { Provider } from '../../../models/Entities/Provider/Provider';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { ErrorType } from '../../../models/HttpError';

import PractitionerSelectorComponent from '../../../commons/component/Inputs/PractitionerSelector/PractitionerSelectorComponent';
import ProviderSelectorComponent from '../../../commons/component/Inputs/ProviderSelector/ProviderSelectorComponent';
import MedicalServiceSelectorComponent from '../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent';
import ContractStagesSelectorComponent from '../../../commons/component/Inputs/ContractStagesSelector/ContractStages.Selector.Component';
import ChangeTypeOfConsumptionComponent from '../ChangeTypeOfConsumption/ChangeTypeOfConsumption.Component';

// guía
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// formato
import { marksOf } from './ConsumptionDetails.Format';

export interface ClassificationProps {
    consumption: ProtocolConsumption;
    protocolId: number;
    contractId: number | undefined;
    isGrouped: boolean;
    GetPersonByIdentifier: (identifier: string) => Practitioner | undefined;

    // marcas
    ChangeProcedure: (protocolId: number, consumptionId: string, isProcedure: boolean, changeAll: boolean) => void;
    ChangeAdditional: (protocolId: number, consumptionId: string, isAdditional: boolean, changeAll: boolean) => void;
    /** Condición del consumo: Normal, falla de selección, discontinuación temprana o seguimiento (ProtocolConsumptionConditionEnum). */
    SetConsumptionCondition: (protocolId: number, consumptionId: string, condition: number) => void;
    SetConsumptionGrouping: (protocolId: number, consumptionId: string, group: boolean) => void;

    // asociaciones
    ChangeVisit: (protocolId: number, consumptionId: string, stageId: number) => void;
    ChangeMedicalService: (protocolId: number, consumptionId: string, medicalServiceId: number) => void;
    ChangeProvider: (protocolId: number, consumptionId: string, provider: Provider) => void;
    ChangeProducer: (protocolId: number, consumptionId: string, userId: string) => void;
    isChangingMedicalService: boolean;
    failChangingMedicalService: boolean;
    errorChangingMedicalService: ErrorType | null;
    isChangingProvider: boolean;
    failChangingProvider: boolean;
    errorChangingProvider: ErrorType | null;
    isChangingProducer: boolean;
    failChangingProducer: boolean;
    errorChangingProducer: ErrorType | null;
}

type Mark = 'procedure' | 'additional';

/** Tarjeta de asociación de la guía: etiqueta con ícono, valor o vacío, y editor en línea al elegir "Cambiar". */
const AssociationField = ({ icon, label, value, emptyLabel, editing, onEdit, children, wide, testId, t }: {
    icon: React.ReactNode;
    label: string;
    value: string | undefined;
    emptyLabel: string;
    editing: boolean;
    onEdit?: () => void;
    children?: React.ReactNode;
    wide?: boolean;
    testId?: string;
    t: (key: string) => string;
}) => (
    <div className={`cdt-assoc${editing ? ' is-editing' : ''}${value ? '' : ' is-empty'}${wide ? ' cdt-assoc--wide' : ''}`} data-testid={testId}>
        <span className="cdt-assoc__label"><span className="cdt-assoc__icon" aria-hidden="true">{icon}</span>{label}</span>
        {!editing ? (
            <div className="cdt-assoc__body">
                <span className={value ? 'cdt-assoc__value' : 'cdt-assoc__empty'}>{value || emptyLabel}</span>
                {onEdit && (
                    <Button appearance="subtle" size="small" icon={<Edit16Regular />} onClick={onEdit}>
                        {value ? t('consumptionDetails.change') : t('consumptionDetails.associate')}
                    </Button>
                )}
            </div>
        ) : (
            <div className="cdt-assoc__editor">{children}</div>
        )}
    </div>
);

const EditorActions = ({ busy, disabled, onConfirm, onCancel, t }: { busy: boolean; disabled: boolean; onConfirm: () => void; onCancel: () => void; t: (key: string) => string }) => (
    <div className="cdt-actions">
        <Button appearance="primary" size="small" disabled={busy || disabled} onClick={onConfirm} icon={busy ? <Spinner size="extra-tiny" /> : undefined}>
            {t('consumptionDetails.confirm')}
        </Button>
        <Button appearance="subtle" size="small" disabled={busy} onClick={onCancel}>{t('common.cancel')}</Button>
    </div>
);

const ErrorBanner = ({ title, error }: { title: string; error: ErrorType | null | undefined }) => (
    <GuideMessageBar intent="error" layout="multiline">
        <MessageBarBody>
            <MessageBarTitle>{title}</MessageBarTitle>
            {error?.ErrorMessage || error?.Errors?.join(', ')}
        </MessageBarBody>
    </GuideMessageBar>
);

/** Profesional del consumo: avatar, nombre y especialidad, con cambio en línea. Se usa también solo, para consumos agrupados. */
export const ProducerCard = (props: Pick<ClassificationProps, 'consumption' | 'protocolId' | 'isGrouped' | 'GetPersonByIdentifier' | 'ChangeProducer' | 'isChangingProducer' | 'failChangingProducer' | 'errorChangingProducer'>) => {
    const [t] = useTranslation();
    const [editing, setEditing] = useState(false);
    const [newProducer, setNewProducer] = useState<Practitioner | undefined>(undefined);
    const wasChanging = useRef(false);

    useEffect(() => {
        if (wasChanging.current && !props.isChangingProducer && !props.failChangingProducer) { setEditing(false); setNewProducer(undefined); }
        wasChanging.current = props.isChangingProducer;
    }, [props.isChangingProducer, props.failChangingProducer]);

    const person = props.consumption.producer ? props.GetPersonByIdentifier(props.consumption.producer) : undefined;

    return (
        <AssociationField
            icon={<Person16Regular />}
            label={t('consumptionDetails.producer')}
            value={person?.fullName || props.consumption.producer || undefined}
            emptyLabel={t('consumptionDetails.noProducer')}
            editing={editing}
            onEdit={props.isGrouped ? undefined : () => setEditing(true)}
            wide
            testId="cdt-producer"
            t={t}
        >
            {props.failChangingProducer && <ErrorBanner title={t('common.error')} error={props.errorChangingProducer} />}
            {person && (
                <div className="cdt-person">
                    <Avatar aria-label={person.fullName} name={person.fullName} color="colorful" size={32} />
                    <div className="cdt-person__text"><span className="cdt-person__name">{person.fullName}</span><span className="cdt-person__role">{person.speciality}</span></div>
                </div>
            )}
            <PractitionerSelectorComponent
                onChangeSelection={(practitioners: Practitioner[] | undefined) => setNewProducer(practitioners?.[0])}
                SelectedPractitioners={newProducer ? [newProducer.userId] : props.consumption.producer ? [props.consumption.producer] : []}
                MultiselectEnable={false}
            />
            <EditorActions
                busy={props.isChangingProducer}
                disabled={!newProducer?.userId}
                onConfirm={() => { if (newProducer?.userId) props.ChangeProducer(props.protocolId, props.consumption.id, newProducer.userId); }}
                onCancel={() => { setEditing(false); setNewProducer(undefined); }}
                t={t}
            />
        </AssociationField>
    );
};

/**
 * Clasificación del consumo (guía): tipo de visita, marcas como botones de alternancia que se
 * guardan al tocarlas, y las asociaciones (visita, servicio, proveedor, profesional) como tarjetas
 * con edición en línea.
 */
export const ConsumptionDetailsClassification = (props: ClassificationProps) => {
    const [t] = useTranslation();
    const c = props.consumption;
    const marks = marksOf(c);

    const [changeAll, setChangeAll] = useState(false);
    const [pendingMark, setPendingMark] = useState<Mark | null>(null);
    const wasUpdating = useRef(false);

    const [editingStage, setEditingStage] = useState(false);
    const [newStage, setNewStage] = useState<ContractStage | undefined>(undefined);
    const [editingService, setEditingService] = useState(false);
    const [newService, setNewService] = useState<AvailableMedicalService | undefined>(undefined);
    const [editingProvider, setEditingProvider] = useState(false);
    const [newProvider, setNewProvider] = useState<Provider | undefined>(undefined);

    // Procedimiento y adicional comparten `isUpdating`: se recuerda cuál se tocó para girar sólo ese.
    useEffect(() => {
        const updating = !!c.isUpdating;
        if (wasUpdating.current && !updating) setPendingMark(null);
        wasUpdating.current = updating;
    }, [c.isUpdating]);

    useEffect(() => { if (editingStage && c.isUpdatedSuccessfully) { setEditingStage(false); setNewStage(undefined); } }, [c.isUpdatedSuccessfully]); // eslint-disable-line react-hooks/exhaustive-deps

    const wasChangingService = useRef(false);
    useEffect(() => {
        if (wasChangingService.current && !props.isChangingMedicalService && !props.failChangingMedicalService) { setEditingService(false); setNewService(undefined); }
        wasChangingService.current = props.isChangingMedicalService;
    }, [props.isChangingMedicalService, props.failChangingMedicalService]);

    const wasChangingProvider = useRef(false);
    useEffect(() => {
        if (wasChangingProvider.current && !props.isChangingProvider && !props.failChangingProvider) { setEditingProvider(false); setNewProvider(undefined); }
        wasChangingProvider.current = props.isChangingProvider;
    }, [props.isChangingProvider, props.failChangingProvider]);

    const isMarkBusy = (mark: Mark) => pendingMark === mark && !!c.isUpdating;

    const markButton = (key: string, on: boolean, busy: boolean, disabled: boolean, icon: React.ReactNode, label: string, title: string, onClick: () => void) => (
        <button type="button" className={`cdt-mark${on ? ' is-on' : ''}`} aria-pressed={on} aria-busy={busy || undefined} disabled={busy || disabled} title={title} onClick={onClick} data-testid={`cdt-mark-${key}`}>
            {busy ? <Spinner size="extra-tiny" /> : icon}
            <span>{label}</span>
        </button>
    );

    // Condición actual (sin dato, el backend la trata como Normal). Mientras cambia, giran todas las opciones.
    const currentCondition: number = c.condition ?? ProtocolConsumptionConditionEnum.Normal;
    const changingCondition = !!c.isChangingEarlyDiscontinuation || !!c.isMarkingFollowUp;
    const conditionButton = (value: number, key: string, icon: React.ReactNode, label: string, title: string) => {
        const on = currentCondition === value;
        return (
            <button
                type="button"
                role="radio"
                className={`cdt-mark${on ? ' is-on' : ''}`}
                aria-checked={on}
                aria-busy={changingCondition || undefined}
                disabled={changingCondition || on}
                title={title}
                onClick={() => props.SetConsumptionCondition(props.protocolId, c.id, value)}
                data-testid={`cdt-condition-${key}`}
            >
                {changingCondition && !on ? <Spinner size="extra-tiny" /> : icon}
                <span>{label}</span>
            </button>
        );
    };

    const markError = c.failOnEarlyDiscontinuationChange || c.failOnGroupingChange || c.FailMarkingFollowUp || (c.failOnUpdating && !editingStage);
    const markErrorText = c.ErrorOnEarlyDiscontinuationChange?.ErrorMessage
        || c.ErrorOnGroupingChange?.ErrorMessage
        || (c.FailMarkingFollowUp
            ? (c.ErrorMarkingFollowUp?.ErrorCode === 409 ? t('consumptionDetails.alreadyFollowUp') : c.ErrorMarkingFollowUp?.ErrorMessage)
            : c.error?.ErrorMessage);

    return (
        <div className="cdt-classification" data-testid="cdt-classification">
            <ChangeTypeOfConsumptionComponent ProtocolConsumptionId={c.id} ProtocolId={props.protocolId} />

            <p className="cdt-hint">{t('consumptionDetails.marksHint')}</p>

            <div className="cdt-mark-group">
                <span className="cdt-mark-group__title">{t('consumptionDetails.classification')}</span>
                <div className="cdt-marks" role="group" aria-label={t('consumptionDetails.classificationAria')}>
                    {markButton('procedure', marks.isProcedure, isMarkBusy('procedure'), false, <AlertOn16Regular />, t('consumptionDetails.procedure'), t('consumptionDetails.procedureTooltip'), () => { setPendingMark('procedure'); props.ChangeProcedure(props.protocolId, c.id, !marks.isProcedure, changeAll); })}
                    {markButton('additional', marks.isAdditional, isMarkBusy('additional'), false, <Important16Regular />, t('consumptionDetails.additional'), t('consumptionDetails.additionalTooltip'), () => { setPendingMark('additional'); props.ChangeAdditional(props.protocolId, c.id, !marks.isAdditional, changeAll); })}
                </div>
                <Checkbox className="cdt-scope" checked={changeAll} onChange={(_event, data) => setChangeAll(!!data.checked)} label={t('consumptionDetails.applyToSameType')} />
                <p className="cdt-hint">{changeAll ? t('consumptionDetails.applyAllOn') : t('consumptionDetails.applyAllOff')}</p>
            </div>

            {/* Condición: una sola a la vez (Normal, falla de selección, discontinuación temprana,
                seguimiento), por el endpoint genérico de condición. Elegir la que ya está no hace nada. */}
            <div className="cdt-mark-group">
                <span className="cdt-mark-group__title">{t('consumptionDetails.conditionSection')}</span>
                <div className="cdt-marks" role="radiogroup" aria-label={t('consumptionDetails.conditionAria')}>
                    {conditionButton(ProtocolConsumptionConditionEnum.Normal, 'normal', <Checkmark16Regular />, t('consumptionDetails.conditionNormal'), t('consumptionDetails.conditionNormalTooltip'))}
                    {conditionButton(ProtocolConsumptionConditionEnum.ScreeningFailure, 'screening-failure', <Warning16Regular />, t('consumptionDetails.screeningFailure'), t('consumptionDetails.screeningFailureTooltip'))}
                    {conditionButton(ProtocolConsumptionConditionEnum.EarlyDiscontinuation, 'early', <ArrowExit20Regular />, t('consumptionDetails.earlyTermination'), t('consumptionDetails.earlyTerminationTooltip'))}
                    {conditionButton(ProtocolConsumptionConditionEnum.FollowUp, 'followup', <Flag16Regular />, t('consumptionDetails.followUp'), t('consumptionDetails.followUpTooltip'))}
                </div>
                <p className="cdt-hint">{t('consumptionDetails.conditionHint')}</p>
            </div>

            {/* Agrupación: aparte de la condición, porque es otro dato (vive en parentGroupingId). */}
            {!props.isGrouped && (
                <div className="cdt-mark-group">
                    <span className="cdt-mark-group__title">{t('consumptionDetails.groupingSection')}</span>
                    <div className="cdt-marks" role="group" aria-label={t('consumptionDetails.groupingAria')}>
                        {markButton('grouped', marks.isGroupedInVisit, !!c.isChangingGrouping, false, <BranchFork16Regular />, t('consumptionDetails.grouped'), t('consumptionDetails.groupedTooltip'), () => props.SetConsumptionGrouping(props.protocolId, c.id, !marks.isGroupedInVisit))}
                    </div>
                    <p className="cdt-hint">{t('consumptionDetails.groupingHint')}</p>
                </div>
            )}

            {markError && (
                <GuideMessageBar intent="error" layout="multiline" data-testid="cdt-mark-error">
                    <MessageBarBody>
                        <MessageBarTitle>{t('consumptionDetails.markError')}</MessageBarTitle>
                        {markErrorText}
                    </MessageBarBody>
                </GuideMessageBar>
            )}

            <div className="cdt-assoc-grid">
                {props.contractId && (
                    <AssociationField icon={<CalendarLtr16Regular />} label={t('consumptionDetails.visit')} value={c.stageName || undefined} emptyLabel={t('consumptionDetails.noVisit')} editing={editingStage} onEdit={() => setEditingStage(true)} testId="cdt-assoc-visit" t={t}>
                        {c.failOnUpdating && <ErrorBanner title={t('common.error')} error={c.error} />}
                        <ContractStagesSelectorComponent
                            ContractId={props.contractId}
                            onSelectedContractStage={(stages: ContractStage[]) => setNewStage(stages?.[0])}
                            ContractStagesSelected={newStage ? [newStage.id] : c.stageId ? [c.stageId] : []}
                            MultiselectEnable={false}
                            noteText={undefined}
                        />
                        <EditorActions busy={!!c.isUpdating} disabled={!newStage} onConfirm={() => { if (newStage?.id) props.ChangeVisit(c.protocolId, c.id, newStage.id); }} onCancel={() => { setEditingStage(false); setNewStage(undefined); }} t={t} />
                    </AssociationField>
                )}

                <AssociationField icon={<Stethoscope20Regular />} label={t('consumptionDetails.medicalService')} value={c.medicalServiceId ? c.medicalServiceName : undefined} emptyLabel={t('consumptionDetails.noMedicalService')} editing={editingService} onEdit={() => setEditingService(true)} testId="cdt-assoc-service" t={t}>
                    {props.failChangingMedicalService && <ErrorBanner title={t('common.error')} error={props.errorChangingMedicalService} />}
                    <MedicalServiceSelectorComponent
                        onSelectedServices={(services: AvailableMedicalService[] | undefined) => setNewService(services?.[0])}
                        MedicalServicesSelectedIds={newService ? [newService.id] : c.medicalServiceId ? [c.medicalServiceId] : []}
                        MultiselectEnable={false}
                    />
                    <EditorActions busy={props.isChangingMedicalService} disabled={!newService} onConfirm={() => { if (newService?.id) props.ChangeMedicalService(props.protocolId, c.id, newService.id); }} onCancel={() => { setEditingService(false); setNewService(undefined); }} t={t} />
                </AssociationField>

                {!props.isGrouped && (
                    <AssociationField icon={<Building16Regular />} label={t('consumptionDetails.provider')} value={c.providerId ? c.providerName : undefined} emptyLabel={t('consumptionDetails.noProvider')} editing={editingProvider} onEdit={() => setEditingProvider(true)} testId="cdt-assoc-provider" t={t}>
                        {props.failChangingProvider && <ErrorBanner title={props.errorChangingProvider?.ErrorMessage || t('common.error')} error={{ ErrorCode: 0, ErrorMessage: t('consumptionDetails.verifyData'), Errors: [] } as ErrorType} />}
                        <ProviderSelectorComponent
                            onChangeSelection={(providers: Provider[] | undefined) => setNewProvider(providers?.[0])}
                            ProviderSelected={newProvider ? [newProvider.person.id] : c.providerId ? ([c.providerId] as string[]) : []}
                            MultiselectEnable={false}
                        />
                        <EditorActions busy={props.isChangingProvider} disabled={!newProvider} onConfirm={() => { if (newProvider) props.ChangeProvider(props.protocolId, c.id, newProvider); }} onCancel={() => { setEditingProvider(false); setNewProvider(undefined); }} t={t} />
                    </AssociationField>
                )}

                <ProducerCard {...props} />
            </div>
        </div>
    );
};

export default ConsumptionDetailsClassification;
